import { useState, useEffect } from "react"
import { Link } from "react-router-dom"
import Card from "@mui/material/Card"
import IconButton from "@mui/material/IconButton"
import InputAdornment from "@mui/material/InputAdornment"
import Visibility from "@mui/icons-material/Visibility"
import VisibilityOff from "@mui/icons-material/VisibilityOff"
import MDBox from "components/MDBox"
import MDTypography from "components/MDTypography"
import MDInput from "components/MDInput"
import MDButton from "components/MDButton"
import BasicLayout from "../components/BasicLayout"

import { loginUser } from "../../api/login"
import { getMaintenance } from "../../api/get_maintenance"
import { getContractInformation } from "../../api/get_contract_information"
import { useLoading } from "../LoadingContext"
import { useNavigate } from "react-router-dom"
import { HELP_PAGE } from "../../config"

function Login() {
  const [showPassword, setShowPassword] = useState(false)
  const { startLoading, stopLoading } = useLoading()
  const [email, setEmail] = useState<string>("")
  const [password, setPassword] = useState<string>("")
  const [mailErrorMessage, setMailErrorMessage] = useState<string>("")
  const [passwordErrorMessage, setPasswordErrorMessage] = useState<string>("")
  const [maintenance, setMaintenance] = useState<boolean>(false)
  const [contractInformationUrl, setContractInformationUrl] = useState<string>("")
  const navigate = useNavigate()

  // ログイン画面遷移時に、セッションコードを初期化
  localStorage.setItem("session_code", "")

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword)
  }
  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value)
  }

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value)
  }

  const validate = () => {
    let hasError = false

    // 必須項目のバリデーション
    if (!email || email == "") {
      setMailErrorMessage("必須項目が入力されていません")
      hasError = true
    } else if (email.length > 64) {
      setMailErrorMessage("入力文字数は64文字以内です")
      hasError = true
    } else {
      setMailErrorMessage("")
    }

    if (!password || password == "") {
      setPasswordErrorMessage("必須項目が入力されていません")
      hasError = true
    } else if (password.length > 32 || password.length < 12) {
      setPasswordErrorMessage("入力文字数は12文字から32文字以内です")
      hasError = true
    } else {
      setPasswordErrorMessage("")
    }

    return !hasError
  }

  const handleLogin = async () => {
    //console.log("validate() :::: ", validate());
    if (validate()) {
      try {
        startLoading()
        const {
          session_code,
          name,
          account_type,
          auth_account,
          auth_facility,
          auth_list,
          auth_setting,
          auth_logo,
          is_initialize,
          medical_record_display_type
        } = await loginUser(email, password)
        localStorage.setItem("name", name)
        localStorage.setItem("session_code", session_code)
        localStorage.setItem("account_type", account_type)
        localStorage.setItem("auth_account", auth_account)
        localStorage.setItem("auth_facility", auth_facility)
        localStorage.setItem("auth_list", auth_list)
        localStorage.setItem("auth_setting", auth_setting)
        localStorage.setItem("auth_logo", auth_logo)
        localStorage.setItem("is_initialize", is_initialize)
        localStorage.setItem("medical_record_display_type", medical_record_display_type)
        localStorage.setItem("show_mode_catv", "-")
        localStorage.setItem("show_mode_estate", "-")
        localStorage.setItem("show_mode_catv_name", "-")
        localStorage.setItem("show_mode_estate_name", "-")
        localStorage.setItem("view_mode", "0")

        setMailErrorMessage("")
        setPasswordErrorMessage("")
        // 一旦認証コード周りは蓋閉じ
        navigate("/auth-code")
        // navigate("/dashboard");
      } catch (error: any) {
        // エラーハンドリング
        //console.log(error);
        if (error.response && error.response.status === 401) {
          const errorDetail = error.response.data.detail
          setMailErrorMessage(errorDetail.message)
          setPasswordErrorMessage("")
        } else if (error.response && error.response.status === 402) {
          const errorDetail = error.response.data.detail
          setMailErrorMessage("")
          setPasswordErrorMessage(errorDetail.message)
        } else if (error.response && error.response.status === 423) {
          // ロックされている
          setMailErrorMessage("")
          setPasswordErrorMessage("")
          localStorage.setItem("mail", email)
          navigate("/lock")
        } else {
          setPasswordErrorMessage("サーバーへの接続に失敗しました")
        }
      } finally {
        stopLoading()
      }
    }
  }

  useEffect(() => {
    const fetchInfo = async () => {
      try {
        startLoading()
        const maintenance = await getMaintenance()
        if (maintenance.maintenance == 1) {
          setMaintenance(true)
          if (maintenance.maintenance_memo != "") {
            setPasswordErrorMessage(maintenance.maintenance_memo)
          }
        }

        //契約時資料のダウンロードリンク
        const contractInformation = await getContractInformation()
        setContractInformationUrl(contractInformation.url)
      } catch (error: any) {
        const errorDetail = error.response.data.detail
        setMailErrorMessage("")
        setPasswordErrorMessage(errorDetail.message)
      } finally {
        stopLoading()
      }
    }
    fetchInfo()
  }, [])

  return (
    <BasicLayout>
      <Card>
        <MDBox textAlign="center" pt={6} pb={4}>
          <MDTypography variant="h4" fontWeight="medium" mb={3}>
            でんぱでみてるくん
          </MDTypography>
        </MDBox>
        {maintenance && (
          <MDBox
            color="#FF0000" // テキスト色
            mb={2} // 下マージン
            fontSize="1em" // フォントサイズ
            sx={{ textAlign: "center", width: "100%" }}
          >
            メンテナンス中
          </MDBox>
        )}
        <MDBox px={6} pb={4}>
          <MDBox component="form" role="form">
            <MDBox mb={1}>
              <MDInput
                type="email"
                label="Email Address"
                fullWidth
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "4px",
                    "& fieldset": {
                      borderColor: "rgba(0, 0, 0, 0.1)"
                    },
                    "&:hover fieldset": {
                      borderColor: "rgba(0, 0, 0, 0.2)"
                    }
                  }
                }}
                onChange={handleEmailChange}
              />
            </MDBox>
            {mailErrorMessage && (
              <MDBox
                color="#FF0000" // テキスト色
                mb={2} // 下マージン
                fontSize="0.7em" // フォントサイズ
              >
                {mailErrorMessage}
              </MDBox>
            )}
            <MDBox mb={1}>
              <MDInput
                type={showPassword ? "text" : "password"}
                label="Password"
                fullWidth
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "4px",
                    "& fieldset": {
                      borderColor: "rgba(0, 0, 0, 0.1)"
                    },
                    "&:hover fieldset": {
                      borderColor: "rgba(0, 0, 0, 0.2)"
                    }
                  }
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleClickShowPassword}
                        edge="end"
                        sx={{ color: "rgba(0, 0, 0, 0.54)" }}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
                onChange={handlePasswordChange}
              />
            </MDBox>
            {passwordErrorMessage && (
              <MDBox
                color="#FF0000" // テキスト色
                mb={2} // 下マージン
                fontSize="0.7em" // フォントサイズ
              >
                {passwordErrorMessage}
              </MDBox>
            )}
            <MDBox mt={4} mb={2}>
              <MDButton
                variant="contained"
                color="info"
                fullWidth
                sx={{
                  borderRadius: "4px",
                  textTransform: "none",
                  fontSize: "1rem",
                  padding: "0.75rem 0",
                  backgroundColor: "#1976d2",
                  "&:hover": {
                    backgroundColor: "#1565c0"
                  }
                }}
                onClick={() => {
                  //console.log("Login button clicked!");
                  handleLogin()
                }}
              >
                Login
              </MDButton>
            </MDBox>
            <MDBox textAlign="right">
              <MDTypography
                component={Link}
                to="/reset-password"
                variant="button"
                color="info"
                fontWeight="regular"
                sx={{
                  fontSize: "0.875rem",
                  textDecoration: "none",
                  "&:hover": {
                    textDecoration: "underline"
                  }
                }}
              >
                パスワードを忘れた方へ
              </MDTypography>
            </MDBox>
            {contractInformationUrl !== "" && (
              <MDBox textAlign="right">
                <a
                  href={contractInformationUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ fontSize: "0.875rem", textDecoration: "none" }}
                >
                  お申込みはコチラ
                </a>
              </MDBox>
            )}
          </MDBox>
        </MDBox>
      </Card>
      <MDBox textAlign="right">
        <a
          href={HELP_PAGE}
          target="_blank"
          rel="noopener noreferrer"
          style={{ fontSize: "0.65em", textDecoration: "none", marginRight: "20px" }}
        >
          ヘルプ
        </a>
        <a
          href={HELP_PAGE + "privacy"}
          target="_blank"
          rel="noopener noreferrer"
          style={{ fontSize: "0.65em", textDecoration: "none" }}
        >
          プライバシーポリシー
        </a>
      </MDBox>
    </BasicLayout>
  )
}

export default Login
